/**
 * Welcome.js - Main welcome page with two-column layout
 */
class Welcome {
  constructor(container) {
    this.container = container;
    this.featuredApps = [];
  }

  /**
   * Fetch featured apps from the API
   */
  async fetchFeaturedApps() {
    try {
      // Determine the correct API base URL
      let apiUrl;
      const isDevelopment = window.location.hostname === 'localhost' || 
                           window.location.hostname === '127.0.0.1' ||
                           window.location.hostname.includes('localhost') ||
                           window.location.port === '3000' || 
                           window.location.port === '3001' ||
                           window.location.port === '3002' ||
                           window.location.port === '3003' ||
                           window.location.port === '3004' ||
                           window.location.port === '3005' ||
                           window.location.port === '3006' ||
                           window.location.port === '3007' ||
                           window.location.port === '3008' ||
                           window.location.port === '3009' ||
                           window.location.port === '3010';
      
      if (isDevelopment) {
        // Development: Use the local Astro dev server
        apiUrl = 'http://localhost:4321/api/apps/VIBED_CREATIONS.json';
      } else {
        // Production: Use the actual build location
        apiUrl = '/file/ologies/api/apps/VIBED_CREATIONS.json';
      }
      
      const response = await fetch(apiUrl);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.apps && Array.isArray(data.apps)) {
          // Filter for featured apps, sort by publishDate (newest first), take max 3
          this.featuredApps = data.apps
            .filter(app => app.featured === true)
            .sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate))
            .slice(0, 3)
            .map(app => ({
              slug: app.slug,
              title: app.title,
              description: app.description,
              type: app.type,
              appIconUrl: app.appIconUrl,
              websiteUrl: app.websiteUrl,
              publishDate: app.publishDate
            }));
        }
      }
    } catch (error) {
      console.error('Failed to fetch featured apps:', error);
      // Use fallback apps if API fails
      this.featuredApps = [
        {
          slug: 'greencheck',
          title: 'GreenCheck.app',
          description: 'Your personal ingredient detective that scans products and gives you personalized health alerts',
          type: 'mobile',
          appIconUrl: '',
          websiteUrl: '#/apps/GreenCheck.app'
        }
      ];
    }
  }

  /**
   * Generate showcase HTML dynamically
   */
  generateShowcaseHTML() {
    if (this.featuredApps.length === 0) {
      return `
        <div class="welcome-showcase">
          <div class="welcome-showcase-empty">
            <p>No featured apps available at the moment.</p>
          </div>
        </div>
      `;
    }

    const showcaseItems = this.featuredApps.map((app, index) => {
      const showcaseId = `showcase-${app.slug}`;
      const imageStyle = app.appIconUrl ? `background-image: url('${app.appIconUrl}'); background-size: cover; background-position: center;` : '';
      
      return `
        <a href="/apps/${app.slug}" class="welcome-showcase-item" id="${showcaseId}">
          <div class="welcome-showcase-image" style="${imageStyle}"></div>
          <div class="welcome-showcase-info">
            <div>
              <span class="welcome-showcase-title">${app.title}</span>
              <span class="welcome-showcase-tag ${app.type.toLowerCase()}">${app.type.toUpperCase()}</span>
            </div>
            <div class="welcome-showcase-description">${app.description}</div>
          </div>
        </a>
      `;
    }).join('');

    return `<div class="welcome-showcase">${showcaseItems}</div>`;
  }

  /**
   * Render the welcome page
   */
  async render() {
    // First render with loading state
    const loadingShowcase = `
      <div class="welcome-showcase">
        <div class="welcome-showcase-loading">
          <p>Loading featured apps...</p>
        </div>
      </div>
    `;

    const welcomeHTML = `
      <div class="welcome-container">
        <div class="welcome-grid">
          <div class="welcome-column">
            <h2 class="welcome-heading">Start</h2>
            <div class="welcome-links">
              <a href="/file/about/the-team.md" class="welcome-link" id="link-team">
                <span class="material-symbols-sharp welcome-icon">graph_2</span>
                <span class="welcome-link-text">Meet the-team.md...</span>
              </a>
              <a href="/file/about/the-biz.info" class="welcome-link" id="link-biz">
                <span class="material-symbols-sharp welcome-icon">business_center</span>
                <span class="welcome-link-text">Understand the-biz.info...</span>
              </a>
              <a href="#" class="welcome-link" id="link-music">
                <span class="material-symbols-sharp welcome-icon">library_music</span>
                <span class="welcome-link-text">Start vibes...</span>
              </a>
              <a href="#" class="welcome-link" id="link-pong">
                <span class="material-symbols-sharp welcome-icon">videogame_asset</span>
                <span class="welcome-link-text">Play pong...</span>
              </a>
            </div>
            
            <h2 class="welcome-heading">More</h2>
            <div class="welcome-links">
              <a href="/file/dm_us/do-it.sh" class="welcome-link" id="link-dm">
                <span class="welcome-link-text">DM us <span class="tilde">~</span></span>
              </a>
              <a href="/file/were_not_sorry/README.sub" class="welcome-link" id="link-readme">
                <span class="welcome-link-text">Read We're not sorry <span class="tilde">~</span></span>
              </a>
            </div>
          </div>
          
          <div class="welcome-column">
            <h2 class="welcome-heading">Showcase</h2>
            ${loadingShowcase}
          </div>
        </div>
      </div>
    `;
    
    this.container.innerHTML = welcomeHTML;
    
    // Fetch featured apps and update showcase
    await this.fetchFeaturedApps();
    
    // Update showcase with dynamic content
    const showcaseContainer = this.container.querySelector('.welcome-showcase');
    if (showcaseContainer) {
      showcaseContainer.outerHTML = this.generateShowcaseHTML();
    }
    
    // Set up event listeners
    this.setupEventListeners();
    
    return this;
  }
  
  /**
   * Set up event listeners
   */
  setupEventListeners() {
    // Music link - starts the goingup command
    const musicLink = this.container.querySelector('#link-music');
    if (musicLink) {
      musicLink.addEventListener('click', (e) => {
        e.preventDefault();
        if (window.terminal && typeof window.terminal.executeCommand === 'function') {
          window.terminal.executeCommand('goingup');
        }
      });
    }
    
    // Pong link - starts the pong command
    const pongLink = this.container.querySelector('#link-pong');
    if (pongLink) {
      pongLink.addEventListener('click', (e) => {
        e.preventDefault();
        if (window.terminal && typeof window.terminal.executeCommand === 'function') {
          window.terminal.executeCommand('ping');
        }
      });
    }
    
    // Dynamic showcase links
    this.featuredApps.forEach(app => {
      const showcaseElement = this.container.querySelector(`#showcase-${app.slug}`);
      if (showcaseElement) {
        showcaseElement.addEventListener('click', (e) => {
          e.preventDefault();
          if (window.router) {
            window.router.navigate(`/apps/${app.slug}`);
          }
        });
      }
    });
  }
}

export default Welcome; 